/*
 * Reading the values a backend refusal carries. A resolver names what it
 * rejected under the error's `extensions`, so a form can explain the refusal
 * instead of echoing its raw message.
 */
import _ from 'lodash';
import { ApolloError, FetchResult } from '@apollo/client';

import {
  ACCESS_LINK_FAILURE_REASONS,
  AuthFailureReason,
  SESSION_FAILURE_REASONS,
} from '../types/auth-failure.types';

interface IGraphQLErrorLike {
  message?: string;
  extensions?: Record<string, unknown> | null;
}

// A non-2xx response (e.g. a 401 for a rejected sign-in) parks its errors on
// `networkError.result` instead of `graphQLErrors`, so both are read.
const graphQLErrorsOf = (error: unknown): IGraphQLErrorLike[] => {
  const { graphQLErrors, networkError } = (error || {}) as {
    graphQLErrors?: IGraphQLErrorLike[];
    networkError?: { result?: { errors?: unknown } } | null;
  };
  const responseErrors = networkError?.result?.errors;

  return [...(graphQLErrors || []), ...(Array.isArray(responseErrors) ? responseErrors : [])];
};

// The first value carried under `key`, or null when no error carries one —
// which the caller surfaces as a general error instead.
export const readErrorExtension = <T>(error: unknown, key: string): T | null => {
  const match = graphQLErrorsOf(error).find((graphQLError) => graphQLError.extensions?.[key] != null);

  return (match?.extensions?.[key] as T) ?? null;
};

// Every value carried under `key`, flattened: one refusal may name several.
// A non-array value is ignored rather than spread — `_.flatMap` would otherwise
// take a bare string apart into one bogus entry per character.
export const readErrorExtensionList = <T>(error: unknown, key: string): T[] =>
  _.flatMap(graphQLErrorsOf(error), (graphQLError) => {
    const value = graphQLError.extensions?.[key];

    return Array.isArray(value) ? (value as T[]) : [];
  });

// The refusal a resolver deliberately wrote for the user, or null when the failure
// was not one. BAD_USER_INPUT is the only code whose message is authored to be read.
export const readUserInputMessage = (error: unknown): string | null => {
  const match = graphQLErrorsOf(error)
    .find((graphQLError) => graphQLError.extensions?.code === 'BAD_USER_INPUT' && graphQLError.message);

  return match?.message || null;
};

// The server refused this bundle because it cannot report every action. Raised before any read or
// write, so every buffered score is kept — only loading a newer bundle clears it.
export const readClientUpdateRequired = (error: unknown): boolean =>
  graphQLErrorsOf(error).some((graphQLError) => graphQLError.extensions?.code === 'CLIENT_UPDATE_REQUIRED');

const readReasonIn = (error: unknown, reasons: AuthFailureReason[]): AuthFailureReason | null => {
  const reason = readErrorExtension<AuthFailureReason>(error, 'reason');

  return reason && reasons.includes(reason) ? reason : null;
};

// The sign-in token was rejected — the session is over, whatever the operation was.
export const readSessionFailure = (error: unknown) => readReasonIn(error, SESSION_FAILURE_REASONS);

// The email/text link's token was rejected; the sign-in, if any, is still good.
export const readAccessLinkFailure = (error: unknown) => readReasonIn(error, ACCESS_LINK_FAILURE_REASONS);

// The report card exists but isn't shared with this parent or link: not sent yet, or access was revoked.
export const readReportCardUnavailable = (error: unknown): boolean =>
  !!readReasonIn(error, [AuthFailureReason.REPORT_CARD_UNAVAILABLE]);

// A send that reached only some of its recipients, as counts in the server's own unit (e.g. players);
// null when the failure was not one.
export const readSendIncomplete = (error: unknown): { sent: number; failed: number } | null => {
  const match = graphQLErrorsOf(error).find((graphQLError) => graphQLError.extensions?.code === 'SEND_INCOMPLETE');
  if (!match) return null;

  return { sent: Number(match.extensions?.sent) || 0, failed: Number(match.extensions?.failed) || 0 };
};

// A mutation run with errorPolicy 'all' resolves with its errors (so its refetches still run); this rejects
// with them, as the default policy would, keeping each error's extensions for the readers above.
export const rejectGraphQLErrors = <T extends FetchResult<unknown>>(result: T): T => {
  if (result.errors?.length) throw new ApolloError({ graphQLErrors: result.errors });
  return result;
};
